import {Suspense} from 'react'
import {ContributorFooter} from './ContributorFooter'
import {graphql, useFragment, useLazyLoadQuery} from 'react-relay'
import type {LazyContributorFooterQuery} from './__generated__/LazyContributorFooterQuery.graphql'
import type {LazyContributorFooter$key} from './__generated__/LazyContributorFooter.graphql'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'

export type LazyContributorFooterProps = {
  repositoryId: string
  sx?: BetterSystemStyleObject
}

type LazyContributorFooterInternalProps = {
  repositoryKey: LazyContributorFooter$key
  sx?: BetterSystemStyleObject
}

export function LazyContributorFooter(props: LazyContributorFooterProps) {
  return (
    <Suspense fallback={<></>}>
      <LazyContributorFooterFetched {...props} />
    </Suspense>
  )
}

export const LazyContributorFooterGraphQLQuery = graphql`
  query LazyContributorFooterQuery($repositoryId: ID!) {
    node(id: $repositoryId) {
      ... on Repository {
        ...LazyContributorFooter
      }
    }
  }
`

function LazyContributorFooterFetched({repositoryId, sx}: LazyContributorFooterProps) {
  const {node} = useLazyLoadQuery<LazyContributorFooterQuery>(
    LazyContributorFooterGraphQLQuery,
    {repositoryId},
    {fetchPolicy: 'store-only'},
  )

  if (!node) return null

  return <LazyContributorFooterInternal repositoryKey={node} sx={sx} />
}

function LazyContributorFooterInternal({repositoryKey, sx}: LazyContributorFooterInternalProps) {
  const {codeOfConductFileUrl, securityPolicyUrl, contributingFileUrl} = useFragment(
    graphql`
      fragment LazyContributorFooter on Repository {
        codeOfConductFileUrl
        securityPolicyUrl
        contributingFileUrl
      }
    `,
    repositoryKey,
  )

  return (
    <ContributorFooter
      codeOfConductFileUrl={codeOfConductFileUrl ?? undefined}
      securityPolicyUrl={securityPolicyUrl ?? undefined}
      contributingFileUrl={contributingFileUrl ?? undefined}
      sx={{...sx}}
    />
  )
}

try{ LazyContributorFooter.displayName ||= 'LazyContributorFooter' } catch {}
try{ LazyContributorFooterFetched.displayName ||= 'LazyContributorFooterFetched' } catch {}
try{ LazyContributorFooterInternal.displayName ||= 'LazyContributorFooterInternal' } catch {}